import { useEffect, useState } from "react";
import { MessageSquare, Send, Users, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

export const SmsCampaignTab = () => {
  const [message, setMessage] = useState("");
  const [audience, setAudience] = useState({ total: 0, configured: false });
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    api
      .get("/admin/sms/audience")
      .then((r) => setAudience(r.data))
      .catch(() => setAudience({ total: 0, configured: false }))
      .finally(() => setLoading(false));
  }, []);

  const send = async () => {
    setSending(true);
    try {
      const r = await api.post("/admin/sms/send", { message });
      toast.success(`Sending to ${r.data.recipients} subscriber${r.data.recipients === 1 ? "" : "s"}.`);
      setMessage("");
      setConfirming(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send SMS campaign.");
    } finally {
      setSending(false);
    }
  };

  const segments = Math.max(1, Math.ceil(message.length / 160));

  return (
    <div className="max-w-2xl space-y-8" data-testid="sms-campaign-tab">
      <div className="flex items-center gap-6">
        <div className="flex items-center gap-3 border border-[#E5E5E5] rounded-sm px-5 py-4">
          <Users strokeWidth={1} className="h-5 w-5 text-[#560216]" />
          <div>
            <div className="text-2xl font-serif" data-testid="sms-audience-count">{loading ? "…" : audience.total}</div>
            <div className="text-[10px] uppercase tracking-[0.15em] text-[#737373]">Text Subscribers</div>
          </div>
        </div>
      </div>

      {!loading && !audience.configured && (
        <div className="flex items-start gap-3 bg-[#fdf6e3] border border-[#e6d9a8] rounded-md p-4 text-sm text-[#7a6314]" data-testid="sms-not-configured">
          <AlertTriangle strokeWidth={1.25} className="h-5 w-5 shrink-0 mt-0.5" />
          <p>Twilio isn't connected yet, so texts can't be sent. Phone numbers are still being collected. Add your Twilio credentials to enable sending.</p>
        </div>
      )}

      <div>
        <label className="eyebrow mb-2 block">Message</label>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value.slice(0, 480))}
          rows={5}
          placeholder="LIRA: New arrivals just dropped ✨ Shop the collection: lirascarves.com. Reply STOP to opt out."
          className="w-full border border-[#E5E5E5] rounded-md px-4 py-3 text-sm outline-none focus:ring-1 focus:ring-black resize-none"
          data-testid="sms-message-input"
        />
        <div className="flex items-center justify-between mt-2 text-xs text-[#737373]">
          <span>{message.length}/480 characters · ~{segments} SMS segment{segments === 1 ? "" : "s"} each</span>
          <span>Standard carrier rates apply</span>
        </div>
      </div>

      {!confirming ? (
        <button
          onClick={() => setConfirming(true)}
          disabled={!message.trim() || !audience.configured || audience.total === 0}
          className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center"
          data-testid="sms-review-button"
        >
          <MessageSquare strokeWidth={1} className="h-4 w-4 mr-2" /> Review &amp; Send
        </button>
      ) : (
        <div className="border border-[#560216]/30 bg-[#560216]/5 rounded-md p-5 space-y-4" data-testid="sms-confirm-box">
          <p className="text-sm text-[#1A1A1A]">
            You're about to text <strong data-testid="sms-confirm-count">{audience.total}</strong> subscriber{audience.total === 1 ? "" : "s"}. This will send immediately and cannot be undone.
          </p>
          <div className="flex items-center gap-3">
            <button onClick={send} disabled={sending} className="btn-primary inline-flex items-center disabled:opacity-50" data-testid="sms-confirm-send-button">
              <Send strokeWidth={1} className="h-4 w-4 mr-2" /> {sending ? "Sending…" : `Send to ${audience.total}`}
            </button>
            <button onClick={() => setConfirming(false)} disabled={sending} className="btn-secondary" data-testid="sms-cancel-button">
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SmsCampaignTab;
